import React from 'react';

import type { DisplaySpec, DisplaySpecField } from '~/components/AiChat/types';
import { ScalarValue } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/renderFieldValue';
import { Text } from '~/components/core/TextComponents';
import cn from '~/Utils/cn';

// Stable contract every structured-output render component receives. Container-agnostic: the
// `structured_output` values plus the CMS-owned `display_spec` describing how to render them.
export interface StructuredOutputRendererProps {
  structured_output: Record<string, unknown>;
  display_spec: DisplaySpec;
}

interface KeyValueRowProps {
  field: DisplaySpecField;
  value: unknown;
  // Lets an unbroken token (an id, a snake_case value) wrap inside the value column instead of pushing
  // the row past its container. `anywhere`, unlike `break-word`, also lowers the column's min-content
  // width, which a flex item cannot shrink below. Inherited, so not for a value that holds a table.
  wrapAnywhere?: boolean;
  // Replaces the `ScalarValue` cell, for a value that is not rendered by its field type.
  children?: React.ReactNode;
}

// One label→value row: the field's label, then its value rendered by `ScalarValue` (or `children`).
export const KeyValueRow: React.FC<KeyValueRowProps> = ({ field, value, wrapAnywhere, children }) => (
  <div className="flex items-start gap-16 py-12">
    <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="w-2/5 shrink-0">
      {field.label}
    </Text>
    <div className={cn('flex-1', wrapAnywhere && 'wrap-anywhere')}>
      {children ?? <ScalarValue field={field} value={value} />}
    </div>
  </div>
);

/**
 * `key_value` renderer: one insight object's fields as a label→value list in a rounded panel
 * (modeled on ConfidenceSection). Iterates the display-spec fields in configured order, looking each
 * value up in `structured_output` by `field.key` and delegating the per-type cell rendering to
 * `FieldValue` (an object value collapses to text instead). Hairline dividers separate the rows so a
 * long field list scans easily; rows keep a white background (no zebra striping) so the neutral gray
 * N/A chip never blends into a row.
 */
export const StructuredOutputKeyValue: React.FC<StructuredOutputRendererProps> = ({
  structured_output,
  display_spec,
}) => (
  <div className="flex flex-col divide-y divide-slate-500 rounded-xl bg-white px-16">
    {display_spec.fields.map((field) => (
      <KeyValueRow key={field.key} field={field} value={structured_output[field.key]} />
    ))}
  </div>
);
